import { match } from "ts-pattern"
import * as O from "fp-ts/Option"
import * as F from "fp-ts/function"
import { FATAL, TRANSIENT } from "@ssc-booking/api-contracts/src/error-codes"
import { ECommerceEvent } from "../../../features/_common/machines/ecommerce-machine/e-commerce-machine"
import { ErrorKind } from "../notification-machine"
import { DataLoadContext, DataLoadEvents, FatalErrorCode, LoadError, LoadFatalError } from "./types"

export const interceptErrors = <AReq, ASuccess, AErrorCode extends string, AError extends { errorCode: AErrorCode }>(
    knownCases: (
        ctx: DataLoadContext<AReq, ASuccess, AError>,
        ev: DataLoadEvents<AReq, ASuccess, AError>,
    ) => ECommerceEvent | undefined,
    ctx: DataLoadContext<AReq, ASuccess, AError>,
    ev: DataLoadEvents<AReq, ASuccess, AError>,
): ECommerceEvent | undefined =>
    F.pipe(
        knownCases(ctx, ev),
        O.fromNullable,
        O.fold(
            () =>
                match(ev)
                    .with({ type: "LOAD_ERROR" }, (event) => loadError(ctx, event))
                    .with({ type: "LOAD_FATAL_ERROR" }, (event) => loadFatalError(ctx, event))
                    .otherwise(() => undefined),
            F.identity,
        ),
    )

const loadError = <AReq, ASuccess, AErrorCode extends string, AError extends { errorCode: AErrorCode }>(
    ctx: DataLoadContext<AReq, ASuccess, AError>,
    ev: LoadError<AError>,
): ECommerceEvent => ({
    type: "ERROR",
    kind: loadErrorToErrorKind(ev.response.errorCode),
    origin: ctx.id,
    errorCode: ev.response.errorCode,
    info: ev,
    message: `Load error ${ctx.id}`,
})

const knownLoadErrors: Record<string, ErrorKind> = {
    [FATAL]: "fatal",
    [TRANSIENT]: "generic",
}
const loadErrorToErrorKind = (code: string): ErrorKind => knownLoadErrors[code] || "generic"

const knownLoadFatalErrors: Record<FatalErrorCode, ErrorKind> = {
    unknownCountry: "fatal",
    unknownAction: "fatal",
    infrastructure: "fatal",
    retriesExceeded: "fatal",
    // retryable
    parsing: "generic",
    invalidRequest: "generic",
    connectionClosed: "generic",
}

const loadFatalError = <AReq, ASuccess, AError>(
    ctx: DataLoadContext<AReq, ASuccess, AError>,
    ev: LoadFatalError,
): ECommerceEvent => ({
    type: "ERROR",
    kind: knownLoadFatalErrors[ev.errorCode],
    origin: ctx.id,
    errorCode: ev.errorCode,
    info: ev,
    message: `Load fatal error ${ctx.id}: ${ev.message}`,
})
